import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = "kalamai_admin_token";

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchList = useCallback(
    async (q = "") => {
      if (!token) return;
      setLoading(true);
      try {
        const res = await axios.get(`${API}/admin/waitlist`, {
          params: q ? { q } : {},
          headers: { Authorization: `Bearer ${token}` },
        });
        setRows(res.data.rows || []);
        setTotal(res.data.total || 0);
      } catch (err) {
        if (err?.response?.status === 401) {
          toast.error("Session expired. Please login again.");
          setToken("");
          localStorage.removeItem(STORAGE_KEY);
        } else {
          toast.error("Failed to load signups.");
        }
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  useEffect(() => {
    if (token) fetchList();
  }, [token, fetchList]);

  const login = async (e) => {
    e?.preventDefault?.();
    if (!password) {
      toast.error("Enter the admin password.");
      return;
    }
    setLoggingIn(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      if (res.data?.success) {
        localStorage.setItem(STORAGE_KEY, res.data.token);
        setToken(res.data.token);
        setPassword("");
        toast.success("Welcome back!");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid password");
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setToken("");
    setRows([]);
    setTotal(0);
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "waitlist_kalamai.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      toast.error("Export failed.");
    }
  };

  const onSearch = (e) => {
    e?.preventDefault?.();
    fetchList(search.trim());
  };

  if (!token) {
    return (
      <div className="admin-page" data-testid="admin-login-page">
        <div className="admin-login-box">
          <h2>KalamAI Admin</h2>
          <p style={{ color: "var(--muted)", fontSize: 14, marginBottom: 16 }}>Enter admin password to continue.</p>
          <form onSubmit={login}>
            <input
              className="admin-input"
              type="password"
              placeholder="Admin password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-password-input"
              autoFocus
            />
            <button className="admin-btn" type="submit" disabled={loggingIn} data-testid="admin-login-btn">
              {loggingIn ? "Signing in..." : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page" data-testid="admin-dashboard">
      <div className="admin-container">
        <div className="admin-header">
          <h1>KalamAI — Waitlist Admin</h1>
          <div style={{ display: "flex", gap: 12 }}>
            <button className="admin-btn secondary" onClick={logout} data-testid="admin-logout-btn">Logout</button>
          </div>
        </div>

        <div className="admin-stats">
          <div className="admin-stat">
            <div className="label">Total Signups</div>
            <div className="num" data-testid="admin-total-count">{total.toLocaleString("en-IN")}</div>
          </div>
          <div className="admin-stat">
            <div className="label">Showing</div>
            <div className="num">{rows.length.toLocaleString("en-IN")}</div>
          </div>
        </div>

        <form className="admin-actions" onSubmit={onSearch}>
          <input
            className="admin-input"
            style={{ width: 320, marginBottom: 0 }}
            type="text"
            placeholder="Search by name or email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="admin-search-input"
          />
          <button className="admin-btn" type="submit" data-testid="admin-search-btn">Search</button>
          <button className="admin-btn secondary" type="button" onClick={() => { setSearch(""); fetchList(""); }} data-testid="admin-reset-btn">Reset</button>
          <button className="admin-btn" type="button" onClick={exportCsv} data-testid="admin-export-csv-btn">Export CSV</button>
        </form>

        <div className="admin-table-wrap">
          {loading ? (
            <div className="admin-empty">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="admin-empty" data-testid="admin-empty-state">No signups yet.</div>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Date</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody data-testid="admin-rows">
                {rows.map((r, i) => (
                  <tr key={r.id || i}>
                    <td>{i + 1}</td>
                    <td>{r.name}</td>
                    <td>{r.email}</td>
                    <td>{r.phone}</td>
                    <td>{r.created_at ? new Date(r.created_at).toLocaleString("en-IN") : "—"}</td>
                    <td>{r.source}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default Admin;
